import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Modal responsive que se adapta a móviles convirtiéndose en pantalla completa
 */
export default function ResponsiveModal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = "default", // "small", "default", "large"
  fullScreenOnMobile = true
}) {
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (event) => setIsMobileView(event.matches);

    setIsMobileView(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Gestionar scroll cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      const scrollContainer = document.querySelector('[data-app-scroll-container]');
      const previousOverflow = scrollContainer?.style.overflow;
      const previousOverscroll = scrollContainer?.style.overscrollBehavior;
      const previousBodyOverflow = document.body.style.overflow;

      // Bloquear scroll del contenedor principal si existe
      if (scrollContainer) {
        scrollContainer.style.overflow = 'hidden';
        scrollContainer.style.overscrollBehavior = 'contain';
      }

      // Bloquear scroll del body
      document.body.style.overflow = 'hidden';
      
      // Resetear scroll interno del modal
      requestAnimationFrame(() => {
        if (contentRef.current) {
          const contentElement = contentRef.current.querySelector('.modal-content-scroll');
          if (contentElement) {
            contentElement.scrollTop = 0;
          }
        }
      });
      
      return () => {
        // Restaurar cuando se cierra
        document.body.style.overflow = previousBodyOverflow || '';
        if (scrollContainer) {
          scrollContainer.style.overflow = previousOverflow || '';
          scrollContainer.style.overscrollBehavior = previousOverscroll || '';
        }
      };
    }
  }, [isOpen, isMobileView]);

  if (!isOpen) return null;

  const sizeClasses = {
    small: "max-w-md",
    default: "max-w-2xl", 
    large: "max-w-4xl"
  };

  const mobileClass = fullScreenOnMobile 
    ? "md:relative md:inset-auto md:w-auto md:h-auto md:max-w-none md:max-h-none md:rounded-lg mobile-modal" 
    : "";

  const contentMaxHeight = isMobileView ? 'calc(100vh - 4.5rem)' : 'calc(100vh - 8rem)';

  return createPortal(
    <div 
      ref={containerRef}
      className="fixed inset-0 bg-black/50 z-50 flex items-start md:items-center justify-center p-4"
    >
      <div 
        ref={contentRef}
        className={`
          bg-white rounded-lg shadow-xl w-full flex flex-col min-h-0
          ${sizeClasses[size]} ${mobileClass}
        `}
        style={{ maxHeight: isMobileView ? '100vh' : 'calc(100vh - 2rem)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b flex-shrink-0 bg-white">
          <h3 className="text-lg md:text-xl font-semibold text-gray-900">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 touch-target"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content - Scrolleable */}
        <div
          className="modal-content-scroll p-4 md:p-6 overflow-y-auto flex-1"
          style={{ WebkitOverflowScrolling: 'touch', maxHeight: contentMaxHeight }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Componente para grupos de campos de formulario responsive
 */
export function ResponsiveFormGrid({ children, columns = 2, className = "" }) {
  const gridClass = {
    1: "grid grid-cols-1",
    2: "grid grid-cols-1 md:grid-cols-2", 
    3: "grid grid-cols-1 md:grid-cols-3",
    4: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
  };

  return (
    <div className={`${gridClass[columns]} gap-4 md:gap-6 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Campo de formulario responsive
 */
export function ResponsiveFormField({ 
  label, 
  children, 
  required = false, 
  error = null,
  className = "" 
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}

/**
 * Botones de acción responsive
 */
export function ResponsiveActionButtons({ 
  primaryLabel = "Guardar",
  primaryOnClick,
  secondaryLabel = "Cancelar", 
  secondaryOnClick,
  primaryDisabled = false,
  primaryLoading = false,
  className = ""
}) {
  return (
    <div className={`flex flex-col-reverse md:flex-row gap-3 md:gap-4 pt-4 md:pt-6 ${className}`}>
      <button
        type="button"
        onClick={secondaryOnClick}
        className="w-full md:w-auto px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 touch-target"
      >
        {secondaryLabel}
      </button>
      <button
        type="button"
        onClick={primaryOnClick}
        disabled={primaryDisabled || primaryLoading}
        className="w-full md:w-auto px-6 py-3 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed touch-target"
      >
        {primaryLoading ? (
          <span className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            Guardando...
          </span>
        ) : (
          primaryLabel
        )}
      </button>
    </div>
  );
}